import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Empty, Badge, Modal, Button } from "@/components/ui-kit";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";
import { Users, Tags, CheckCircle2, AlertCircle, CalendarDays, BellRing, Clock, MapPin, Dumbbell, XCircle, ClipboardCheck, Megaphone, CreditCard, CalendarClock, FlaskConical } from "lucide-react";

const EVENT_LABELS = { training: "Entraînement", match: "Match", stage: "Stage", event: "Événement" };
const EVENT_COLORS = { training: "blue", match: "green", stage: "purple", event: "yellow" };
const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role;
  if (role === "coach") return <CoachDashboard user={user} />;
  if (role === "parent" || role === "player") return <ParentDashboard user={user} />;
  return <AdminDashboard user={user} />;
}

/* ---------------- Coach ---------------- */
const CoachDashboard = ({ user }) => {
  const [week, setWeek] = useState(null);
  const [openEvent, setOpenEvent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [avModal, setAvModal] = useState(null); // null | "pending" | "answered"

  const load = useCallback(() => {
    api.get("/dashboard/week-trainings").then((r) => setWeek(r.data));
    api.get("/coach/availability-summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const declare = async (eventId, status) => {
    try { await api.post(`/events/${eventId}/availability`, { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const AV_BADGE = {
    confirmed: <Badge color="green"><CheckCircle2 size={12} /> Confirmé</Badge>,
    rejected: <Badge color="red"><XCircle size={12} /> Non retenu</Badge>,
    available: <Badge color="yellow"><Clock size={12} /> Disponible (en attente)</Badge>,
    unavailable: <Badge color="gray"><XCircle size={12} /> Indisponible</Badge>,
  };

  return (
    <div>
      <PageHeader title={`Bonjour, ${user?.name?.split(" ")[0] || ""}`} subtitle="Vos entraînements de la semaine — disponibilité, séance et appel." />
      <NotificationsBanner />
      <MyChildPanel />
      {summary && (
        <div className="grid grid-cols-2 gap-4 mb-8" data-testid="availability-summary">
          <button type="button" data-testid="summary-pending-card" onClick={() => setAvModal("pending")}
            className="text-left rounded-2xl border border-yellow-500/40 bg-card p-5 hover:border-yellow-500 hover:-translate-y-0.5 transition-all">
            <AlertCircle className="text-yellow-500" size={24} />
            <p className="font-display text-4xl font-black mt-3" data-testid="summary-pending">{summary.pending}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">À confirmer<br /><span className="normal-case">(ce mois-ci &amp; le mois prochain — cliquez pour confirmer)</span></p>
          </button>
          <button type="button" data-testid="summary-answered-card" onClick={() => setAvModal("answered")}
            className="text-left rounded-2xl border border-primary/40 bg-card p-5 hover:border-primary hover:-translate-y-0.5 transition-all">
            <CheckCircle2 className="text-primary" size={24} />
            <p className="font-display text-4xl font-black mt-3" data-testid="summary-answered">{summary.answered}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Confirmée<br /><span className="normal-case">(ce mois-ci &amp; le mois prochain — cliquez pour voir)</span></p>
          </button>
        </div>
      )}
      <WeekTrainings week={week} onEventClick={(e) => setOpenEvent(e)}
        renderMeta={(e) => (
          <div className="flex items-center gap-2">
            {e.exercise_count > 0 && <Badge color="blue"><Dumbbell size={12} /> {e.exercise_count} ex.</Badge>}
            <Badge color="green">{e.roster_summary?.confirmed || 0}/{e.roster_summary?.total || 0} confirmés</Badge>
          </div>
        )}
        renderChildren={(e) => {
          const decided = e.my_availability === "confirmed" || e.my_availability === "rejected";
          return (
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-2 flex-wrap" onClick={(ev) => ev.stopPropagation()}>
              <div className="flex items-center gap-2" data-testid={`availability-status-${e.id}`}>
                {AV_BADGE[e.my_availability] || <span className="text-xs text-muted-foreground">Disponibilité non déclarée</span>}
                {e.available_count > 0 && <span className="text-[11px] text-muted-foreground">· {e.available_count} coach(s) dispo</span>}
              </div>
              {decided ? (
                <span className="text-[11px] text-muted-foreground italic">Décision de l'admin prise — non modifiable</span>
              ) : (
                <div className="flex gap-1.5">
                  <button data-testid={`declare-available-${e.id}`} onClick={() => declare(e.id, "available")}
                    className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${e.my_availability === "available" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    <CheckCircle2 size={14} /> Disponible
                  </button>
                  <button data-testid={`declare-unavailable-${e.id}`} onClick={() => declare(e.id, "unavailable")}
                    className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${e.my_availability === "unavailable" ? "bg-destructive text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    <XCircle size={14} /> Indisponible
                  </button>
                  {e.my_availability && <button data-testid={`withdraw-availability-${e.id}`} onClick={() => declare(e.id, null)}
                    className="h-8 px-3 rounded-lg text-xs font-bold text-muted-foreground hover:text-foreground bg-secondary transition-colors">Retirer</button>}
                </div>
              )}
            </div>
          );
        }} />
      {openEvent && <CoachEventModal event={openEvent} onClose={() => { setOpenEvent(null); load(); }} />}
      {avModal && summary && (
        <AvailabilityDetailModal mode={avModal} events={summary.events || []} onDeclare={declare}
          onClose={() => setAvModal(null)} />
      )}
    </div>
  );
};

const AV_TYPE_LABEL = { training: "Entraînement", match: "Match", stage: "Stage", event: "Événement" };
const AvailabilityDetailModal = ({ mode, events, onDeclare, onClose }) => {
  const list = events.filter((e) => (mode === "pending" ? !e.status : !!e.status));
  const AV_BADGE = {
    confirmed: <Badge color="green"><CheckCircle2 size={12} /> Confirmé par l'admin</Badge>,
    rejected: <Badge color="red"><XCircle size={12} /> Non retenu</Badge>,
    available: <Badge color="yellow"><Clock size={12} /> Disponible (en attente)</Badge>,
    unavailable: <Badge color="gray"><XCircle size={12} /> Indisponible</Badge>,
  };
  return (
    <Modal open onClose={onClose} title={mode === "pending" ? "À confirmer" : "Disponibilités confirmées"}>
      <p className="text-sm text-muted-foreground mb-4">
        {mode === "pending"
          ? "Séances et événements pour lesquels vous n'avez pas encore indiqué votre disponibilité (ce mois-ci et le mois prochain)."
          : "Séances et événements pour lesquels vous avez répondu."}
      </p>
      {list.length ? (
        <div className="space-y-2" data-testid="availability-detail-list">
          {list.map((e) => {
            const decided = e.decided;
            return (
              <div key={e.id} data-testid="availability-detail-row" className="p-3 rounded-lg bg-secondary/40 border border-border">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{e.title}</p>
                    <p className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                      <Badge color={EVENT_COLORS[e.type]}>{AV_TYPE_LABEL[e.type] || "Événement"}</Badge>
                      <span>{fmtDate(e.date)}</span>
                      {e.time && <span className="flex items-center gap-1"><Clock size={11} /> {e.time}</span>}
                      {e.location && <span className="flex items-center gap-1"><MapPin size={11} /> {e.location}</span>}
                      {e.category && <span>· {e.category}</span>}
                    </p>
                  </div>
                  {e.status && (AV_BADGE[e.status] || null)}
                </div>
                {decided ? (
                  <span className="text-[11px] text-muted-foreground italic">Décision de l'admin prise — non modifiable</span>
                ) : (
                  <div className="flex gap-1.5">
                    <button data-testid={`modal-declare-available-${e.id}`} onClick={() => onDeclare(e.id, "available")}
                      className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${e.status === "available" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      <CheckCircle2 size={14} /> Disponible
                    </button>
                    <button data-testid={`modal-declare-unavailable-${e.id}`} onClick={() => onDeclare(e.id, "unavailable")}
                      className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${e.status === "unavailable" ? "bg-destructive text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      <XCircle size={14} /> Indisponible
                    </button>
                    {e.status && <button data-testid={`modal-withdraw-${e.id}`} onClick={() => onDeclare(e.id, null)}
                      className="h-8 px-3 rounded-lg text-xs font-bold text-muted-foreground hover:text-foreground bg-secondary transition-colors">Retirer</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : <Empty text={mode === "pending" ? "Tout est à jour, rien à confirmer." : "Aucune disponibilité indiquée pour l'instant."} />}
    </Modal>
  );
};

const NotificationsBanner = () => {
  const [notifs, setNotifs] = useState([]);
  const load = useCallback(() => { api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  const unread = notifs.filter((n) => !n.read);
  if (!unread.length) return null;
  const markRead = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div data-testid="notifications-banner" className="mb-6 rounded-xl border border-primary/40 bg-primary/10 p-4 animate-fade-up">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2"><BellRing className="text-primary" size={18} /><h3 className="font-display font-bold uppercase tracking-tight text-primary text-sm">Notifications</h3></div>
        <button data-testid="mark-notifs-read" onClick={markRead} className="text-xs text-muted-foreground hover:text-foreground underline">Tout marquer comme lu</button>
      </div>
      <div className="space-y-1.5">
        {unread.map((n) => (
          <div key={n.id} data-testid="notification-item" className="flex items-start gap-2 text-sm">
            {n.type === "success" ? <CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /> : n.type === "warning" ? <AlertCircle size={15} className="text-yellow-500 mt-0.5 shrink-0" /> : <BellRing size={15} className="text-muted-foreground mt-0.5 shrink-0" />}
            <span><b>{n.title}</b> — {n.body}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CoachEventModal = ({ event, onClose }) => {
  const [exercises, setExercises] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRoster = useCallback(() => api.get(`/events/${event.id}/attendance-roster`).then((r) => setRoster(r.data.roster || [])), [event.id]);
  useEffect(() => {
    setLoading(true);
    Promise.all([api.get(`/events/${event.id}/exercises`).then((r) => setExercises(r.data)), loadRoster()])
      .finally(() => setLoading(false));
  }, [event.id, loadRoster]);

  const confirm = async (member_id, present) => {
    setRoster((rs) => rs.map((r) => r.member_id === member_id ? { ...r, coach_confirmed: true, present } : r));
    try { await api.post("/attendance/confirm", { event_id: event.id, member_id, present }); }
    catch { toast.error("Erreur"); loadRoster(); }
  };

  return (
    <Modal open onClose={onClose} title={`${event.title} · ${fmtDate(event.date)}${event.time ? " · " + event.time : ""}`}>
      <div className="space-y-6" data-testid="coach-event-detail">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Dumbbell size={14} className="text-primary" /> Exercices prévus</p>
          {loading ? <p className="text-sm text-muted-foreground">Chargement...</p> : exercises.length ? (
            <div className="space-y-1.5">
              {exercises.map((ex, i) => (
                <div key={ex.id} data-testid="coach-detail-exercise" className="flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg bg-secondary/40">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs font-black flex items-center justify-center">{i + 1}</span>
                  <span className="font-semibold">{ex.name}</span>
                  {ex.session_duration ? <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto"><Clock size={11} /> {ex.session_duration} min</span> : null}
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">Aucun exercice planifié. <Link to="/entrainements" className="text-primary underline">Préparer la séance</Link></p>}
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><ClipboardCheck size={14} className="text-primary" /> Appel des joueurs</p>
          {loading ? <p className="text-sm text-muted-foreground">Chargement...</p> : roster.length ? (
            <div className="space-y-1.5">
              {roster.map((p) => (
                <div key={p.member_id} data-testid="coach-roster-row" className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-secondary/40">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{p.name}</p>
                    {p.declared && <p className="text-[11px] text-muted-foreground">Parent : {p.declared === "present" ? "présent" : "absent"}</p>}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button data-testid={`confirm-present-${p.member_id}`} onClick={() => confirm(p.member_id, true)}
                      className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${p.coach_confirmed && p.present ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      <CheckCircle2 size={14} /> Présent
                    </button>
                    <button data-testid={`confirm-absent-${p.member_id}`} onClick={() => confirm(p.member_id, false)}
                      className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${p.coach_confirmed && p.present === false ? "bg-destructive text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      <XCircle size={14} /> Absent
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">Aucun joueur attendu pour cette catégorie.</p>}
        </div>
      </div>
    </Modal>
  );
};

/* ---------------- Parent ---------------- */
const ParentDashboard = ({ user }) => {
  const [upcoming, setUpcoming] = useState(null);
  const [convocations, setConvocations] = useState(null);
  const [summary, setSummary] = useState(null);
  const [stats, setStats] = useState({});
  const [members, setMembers] = useState([]);
  const [detailMode, setDetailMode] = useState(null); // null | "pending" | "confirmed"
  const [selectedChild, setSelectedChild] = useState(null); // null = vue globale
  const [paymentLink, setPaymentLink] = useState("");
  const [balanceDue, setBalanceDue] = useState(0);
  const [installments, setInstallments] = useState([]);

  const load = useCallback(() => {
    api.get("/parent/upcoming-events").then((r) => setUpcoming(r.data)).catch(() => {});
    api.get("/parent/convocations").then((r) => setConvocations(r.data)).catch(() => {});
    api.get("/parent/week-summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/attendance/stats/all").then((r) => setStats(r.data)).catch(() => {});
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/settings/payment-link").then((r) => setPaymentLink(r.data.url || "")).catch(() => {});
    api.get("/fees?status=active").then((r) => {
      let due = 0;
      const insts = [];
      for (const m of (r.data.members || [])) {
        const who = `${m.first_name} ${m.last_name}`.trim();
        for (const f of ([...(m.fees || []), ...(m.events || [])])) {
          due += Number(f.remaining) || 0;
          for (const it of (f.plan?.installments || [])) {
            if (it.status !== "payee") insts.push({ ...it, who, label: f.label });
          }
        }
      }
      insts.sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
      setInstallments(insts);
      setBalanceDue(Math.round(due * 100) / 100);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const declare = async (event_id, member_id, declared) => {
    const patch = (list) => list.map((e) => e.id === event_id
      ? { ...e, participants: e.participants.map((p) => p.member_id === member_id ? { ...p, declared } : p) } : e);
    setUpcoming((u) => u && ({ ...u, events: patch(u.events) }));
    setSummary((s) => s && ({ ...s, events: patch(s.events || []) }));
    setConvocations((c) => c && ({ ...c, convocations: c.convocations.map((cv) =>
      (cv.event_id === event_id && cv.member_id === member_id) ? { ...cv, declared, declared_by: user?.name } : cv) }));
    try { await api.post("/attendance/declare", { event_id, member_id, declared }); load(); }
    catch { toast.error("Erreur"); load(); }
  };

  const memberName = (id) => { const m = members.find((x) => x.member_id === id || x.id === id); return m ? `${m.first_name} ${m.last_name}` : id; };

  return (
    <div>
      <PageHeader title={`Bonjour, ${user?.name?.split(" ")[0] || ""}`} subtitle="Présences de la semaine, statistiques de votre enfant et messagerie du club." />
      <NotificationsBanner />
      {paymentLink && (
        <div data-testid="parent-payment-card" className="mb-6 rounded-2xl border border-primary/40 bg-primary/5 p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
              <CreditCard size={15} className="text-primary" /> Paiement en ligne
            </div>
            {balanceDue > 0 ? (
              <p className="text-sm mt-2">Solde à régler : <span className="font-display text-2xl font-black text-primary align-middle">{balanceDue.toFixed(2)} €</span></p>
            ) : (
              <p className="text-sm mt-2 text-muted-foreground">Vous êtes à jour. Vous pouvez régler un montant libre en ligne.</p>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">Vous serez redirigé vers la page de paiement sécurisée. Indiquez-y le montant à payer.</p>
          </div>
          <button data-testid="dashboard-pay-online-btn" onClick={() => window.open(paymentLink, "_blank", "noopener")}
            className="shrink-0 h-11 px-6 rounded-xl bg-primary text-primary-foreground font-bold flex items-center gap-2 hover:opacity-90 transition-opacity">
            <CreditCard size={18} /> Payer en ligne{balanceDue > 0 ? ` (${balanceDue.toFixed(2)} €)` : ""}
          </button>
        </div>
      )}
      {installments.length > 0 && (
        <div data-testid="parent-installments-card" className="mb-6 rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3">
            <CalendarClock size={15} className="text-primary" /> Plan de paiement — prochaines échéances
          </div>
          <div className="space-y-2">
            {installments.map((it, i) => (
              <div key={i} data-testid={`parent-installment-${i}`}
                className="flex items-center justify-between gap-3 text-sm rounded-lg bg-secondary/40 px-3 py-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{it.label}{members.length > 1 ? <span className="text-muted-foreground font-normal"> · {it.who}</span> : null}</p>
                  <p className="text-xs text-muted-foreground">Échéance le {fmtDate(it.due_date)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-bold">{it.remaining > 0 && it.remaining < it.amount ? `${it.remaining}` : it.amount} €</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${it.status === "en_retard" ? "bg-destructive/15 text-destructive" : "bg-secondary text-muted-foreground"}`}>
                    {it.status === "en_retard" ? "En retard" : "À venir"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {members.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="child-switcher">
          <button data-testid="child-tab-all" onClick={() => setSelectedChild(null)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${!selectedChild ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            Vue globale
          </button>
          {members.map((m) => (
            <button key={m.id} data-testid={`child-tab-${m.id}`} onClick={() => setSelectedChild(m.id)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${selectedChild === m.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {m.first_name} {m.last_name}
            </button>
          ))}
        </div>
      )}
      {summary && (
        <div className="grid grid-cols-2 gap-4 mb-8" data-testid="parent-summary">
          <button type="button" data-testid="parent-pending-card" onClick={() => setDetailMode("pending")}
            className="text-left rounded-2xl border border-yellow-500/40 bg-card p-5 hover:border-yellow-500 hover:-translate-y-0.5 transition-all">
            <AlertCircle className="text-yellow-500" size={24} />
            <p className="font-display text-4xl font-black mt-3" data-testid="parent-pending">{summary.pending}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">À confirmer<br /><span className="normal-case">(cette semaine — cliquez pour valider)</span></p>
          </button>
          <button type="button" data-testid="parent-confirmed-card" onClick={() => setDetailMode("confirmed")}
            className="text-left rounded-2xl border border-primary/40 bg-card p-5 hover:border-primary hover:-translate-y-0.5 transition-all">
            <CheckCircle2 className="text-primary" size={24} />
            <p className="font-display text-4xl font-black mt-3" data-testid="parent-confirmed">{summary.confirmed}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Confirmés<br /><span className="normal-case">(cette semaine — cliquez pour voir)</span></p>
          </button>
        </div>
      )}

      {Object.keys(stats).length > 0 && (
        <div className="mb-8" data-testid="parent-stats">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2"><ClipboardCheck size={20} className="text-primary" /> Statistiques de présence</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(stats).filter(([mid]) => !selectedChild || mid === selectedChild).map(([mid, s]) => {
              const present = s.present || 0; const total = s.total || 0;
              const rate = total ? Math.round((present / total) * 100) : 0;
              return (
                <Card key={mid} data-testid="parent-stat-card">
                  <p className="font-bold mb-2">{memberName(mid)}</p>
                  <div className="flex items-end gap-2 mb-2">
                    <span className="font-display text-3xl font-black text-primary">{rate}%</span>
                    <span className="text-xs text-muted-foreground mb-1">de présence</span>
                  </div>
                  <div className="flex gap-2 flex-wrap text-xs">
                    <Badge color="green">{present} présent{present > 1 ? "s" : ""}</Badge>
                    <Badge color="gray">{total} séance{total > 1 ? "s" : ""} comptée{total > 1 ? "s" : ""}</Badge>
                    {s.excuse ? <Badge color="yellow">{s.excuse} excusé(s)</Badge> : null}
                  </div>
                  <Link to="/presence" className="text-xs text-primary hover:underline mt-3 inline-block">Voir le détail →</Link>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <ConvocationsToConfirm data={convocations} selectedChild={selectedChild} onDeclare={declare} />

      <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2"><CalendarDays size={20} className="text-primary" /> Événements à venir (30 jours)</h2>
      <UpcomingEventsList data={upcoming} selectedChild={selectedChild} onDeclare={declare} />

      {detailMode && summary && (
        <ParentWeekModal mode={detailMode} events={summary.events || []} onDeclare={declare} onClose={() => setDetailMode(null)} />
      )}
    </div>
  );
};

const ConvocationsToConfirm = ({ data, selectedChild, onDeclare }) => {
  if (!data || !data.convocations) return null;
  const list = data.convocations.filter((c) =>
    (!selectedChild || c.member_id === selectedChild) && !c.declared);
  if (!list.length) return null;
  return (
    <div className="mb-6" data-testid="convocations-to-confirm">
      <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
        <Megaphone size={20} className="text-primary" /> Convocations à confirmer
        <span className="ml-1 text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5">{list.length}</span>
      </h2>
      <div className="space-y-3">
        {list.map((c) => (
          <Card key={c.convocation_id} data-testid="convocation-card" className="py-4 border-primary/40 bg-primary/5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge color={EVENT_COLORS[c.type]}>{P_TYPE_LABEL[c.type] || "Événement"}</Badge>
                  <span className="font-semibold text-sm break-words">{c.title}</span>
                </div>
                <p className="text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap mt-1">
                  <span className="font-bold text-foreground">{c.member_name}</span>
                  <span className="flex items-center gap-1"><CalendarDays size={11} /> {fmtDate(c.date)}</span>
                  {c.time && <span className="flex items-center gap-1"><Clock size={11} /> {c.time}</span>}
                  {c.location && <span className="flex items-center gap-1"><MapPin size={11} /> {c.location}</span>}
                  {c.opponent && <span>vs {c.opponent}</span>}
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t border-border">
              <button data-testid={`convocation-present-${c.event_id}-${c.member_id}`} onClick={() => onDeclare(c.event_id, c.member_id, "present")}
                className="flex-1 h-10 rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 bg-primary text-primary-foreground hover:opacity-90 transition-opacity">
                <CheckCircle2 size={16} /> Présent
              </button>
              <button data-testid={`convocation-absent-${c.event_id}-${c.member_id}`} onClick={() => onDeclare(c.event_id, c.member_id, "absent")}
                className="flex-1 h-10 rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 bg-secondary text-muted-foreground hover:text-destructive transition-colors">
                <XCircle size={16} /> Absent
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const UpcomingEventsList = ({ data, selectedChild, onDeclare }) => {
  if (!data) return <Empty text="Chargement..." />;
  const events = (data.events || []).filter((e) =>
    !selectedChild || e.participants.some((p) => p.member_id === selectedChild));
  if (!events.length) return (
    <Card data-testid="upcoming-empty">
      <div className="flex items-center gap-3 text-muted-foreground">
        <CalendarDays size={22} className="text-primary" />
        <p className="text-sm">Aucun événement prévu dans les 30 prochains jours.</p>
      </div>
    </Card>
  );
  return (
    <div className="space-y-3" data-testid="upcoming-events">
      {events.map((e) => (
        <Card key={e.id} data-testid="upcoming-event-card" className="py-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge color={EVENT_COLORS[e.type]}>{P_TYPE_LABEL[e.type] || "Événement"}</Badge>
                <span className="font-semibold text-sm break-words">{e.title}</span>
              </div>
              <p className="text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap mt-1">
                <span className="flex items-center gap-1"><CalendarDays size={11} /> {fmtDate(e.date)}</span>
                {e.time && <span className="flex items-center gap-1"><Clock size={11} /> {e.time}</span>}
                {e.location && <span className="flex items-center gap-1"><MapPin size={11} /> {e.location}</span>}
                {e.opponent && <span>vs {e.opponent}</span>}
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            {e.participants.filter((p) => !selectedChild || p.member_id === selectedChild).map((p) => (
              <div key={p.member_id} data-testid="upcoming-participant-row" className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{p.member_name}</p>
                  {p.declared && <p className="text-[11px] text-muted-foreground">{p.declared === "present" ? "Présent" : "Absent"}{p.declared_by ? ` · par ${p.declared_by}` : ""}</p>}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button data-testid={`upcoming-present-${e.id}-${p.member_id}`} onClick={() => onDeclare(e.id, p.member_id, "present")}
                    className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${p.declared === "present" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    <CheckCircle2 size={14} /> Présent
                  </button>
                  <button data-testid={`upcoming-absent-${e.id}-${p.member_id}`} onClick={() => onDeclare(e.id, p.member_id, "absent")}
                    className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${p.declared === "absent" ? "bg-destructive text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    <XCircle size={14} /> Absent
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
};

const P_TYPE_LABEL = { training: "Entraînement", match: "Match", stage: "Stage", event: "Événement" };
const ParentWeekModal = ({ mode, events, onDeclare, onClose }) => {
  const list = events
    .map((e) => ({ ...e, participants: e.participants.filter((p) => (mode === "pending" ? !p.declared : !!p.declared)) }))
    .filter((e) => e.participants.length);
  return (
    <Modal open onClose={onClose} title={mode === "pending" ? "À confirmer cette semaine" : "Présences confirmées"}>
      <p className="text-sm text-muted-foreground mb-4">
        {mode === "pending" ? "Validez la présence de votre enfant pour ces séances/événements." : "Séances et événements pour lesquels vous avez répondu."}
      </p>
      {list.length ? (
        <div className="space-y-3" data-testid="parent-week-detail">
          {list.map((e) => (
            <div key={e.id} data-testid="parent-week-row" className="p-3 rounded-lg bg-secondary/40 border border-border">
              <div className="mb-2">
                <p className="font-semibold text-sm">{e.title}</p>
                <p className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                  <Badge color={EVENT_COLORS[e.type]}>{P_TYPE_LABEL[e.type] || "Événement"}</Badge>
                  <span>{fmtDate(e.date)}</span>
                  {e.time && <span className="flex items-center gap-1"><Clock size={11} /> {e.time}</span>}
                  {e.location && <span className="flex items-center gap-1"><MapPin size={11} /> {e.location}</span>}
                </p>
              </div>
              {e.participants.map((p) => (
                <div key={p.member_id} className="flex items-center justify-between gap-2 py-1.5">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{p.member_name}</p>
                    {p.declared && <p className="text-[11px] text-muted-foreground">{p.declared === "present" ? "Présent" : "Absent"}</p>}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button data-testid={`modal-present-${p.member_id}`} onClick={() => onDeclare(e.id, p.member_id, "present")}
                      className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${p.declared === "present" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      <CheckCircle2 size={14} /> Présent
                    </button>
                    <button data-testid={`modal-absent-${p.member_id}`} onClick={() => onDeclare(e.id, p.member_id, "absent")}
                      className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${p.declared === "absent" ? "bg-destructive text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      <XCircle size={14} /> Absent
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : <Empty text={mode === "pending" ? "Tout est confirmé, rien à valider." : "Aucune présence confirmée cette semaine."} />}
    </Modal>
  );
};

/* ---------------- Shared week view ---------------- */
const WeekTrainings = ({ week, onEventClick, renderMeta, renderChildren }) => {
  if (!week) return <Empty text="Chargement..." />;
  if (!week.events?.length) return (
    <Card data-testid="week-empty">
      <div className="flex items-center gap-3 text-muted-foreground">
        <CalendarDays size={22} className="text-primary" />
        <p className="text-sm">Aucun entraînement prévu cette semaine.</p>
      </div>
    </Card>
  );
  const byDay = {};
  week.events.forEach((e) => {
    const d = new Date(e.date + "T00:00:00");
    const idx = (d.getDay() + 6) % 7;
    (byDay[idx] = byDay[idx] || []).push(e);
  });
  return (
    <div className="space-y-6" data-testid="week-trainings">
      {Object.keys(byDay).map(Number).sort((a, b) => a - b).map((idx) => (
        <div key={idx}>
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground mb-2">{DAYS[idx]}</h3>
          <div className="space-y-2">
            {byDay[idx].map((e) => (
              <Card key={e.id} data-testid="week-training-card"
                onClick={onEventClick ? () => onEventClick(e) : undefined}
                className={`${onEventClick ? "cursor-pointer hover:border-primary/50" : ""} transition-colors`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="text-center w-12 shrink-0">
                      <p className="font-display text-2xl font-black text-primary leading-none">{e.date?.slice(8, 10)}</p>
                      <p className="text-[10px] uppercase text-muted-foreground">{e.date?.slice(5, 7)}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold truncate">{e.title}{e.category ? ` · ${e.category}` : ""}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                        {e.time && <span className="flex items-center gap-1"><Clock size={11} /> {e.time}</span>}
                        {e.location && <span className="flex items-center gap-1"><MapPin size={11} /> {e.location}</span>}
                      </p>
                    </div>
                  </div>
                  {renderMeta && renderMeta(e)}
                </div>
                {renderChildren && renderChildren(e)}
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminAvailabilitySection = () => {
  const [reqs, setReqs] = useState([]);
  const load = useCallback(() => { api.get("/coach/availability-requests").then((r) => setReqs(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (eventId, coachId, decision) => {
    try { await api.post(`/events/${eventId}/availability/${coachId}`, { decision }); toast.success(decision === "confirmed" ? "Coach confirmé" : "Coach non retenu"); load(); }
    catch { toast.error("Erreur"); }
  };
  if (!reqs.length) return null;
  const STAT = { available: { c: "yellow", t: "Disponible" }, unavailable: { c: "gray", t: "Indisponible" }, confirmed: { c: "green", t: "Confirmé" }, rejected: { c: "red", t: "Non retenu" } };
  return (
    <div className="mb-8" data-testid="admin-availability-section">
      <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2"><ClipboardCheck size={20} className="text-primary" /> Disponibilités des coachs</h2>
      <div className="space-y-3">
        {reqs.map((e) => (
          <Card key={e.id} data-testid="availability-request-card">
            <p className="font-bold mb-3">{e.title} · {fmtDate(e.date)}{e.category ? ` · ${e.category}` : ""}</p>
            <div className="space-y-2">
              {e.coaches.map((c) => (
                <div key={c.coach_user_id} data-testid="availability-coach-row" className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-secondary/40">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold truncate">{c.coach_name}</span>
                    <Badge color={STAT[c.status]?.c}>{STAT[c.status]?.t}</Badge>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button data-testid={`confirm-coach-${c.coach_user_id}`} onClick={() => decide(e.id, c.coach_user_id, "confirmed")}
                      className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${c.status === "confirmed" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      <CheckCircle2 size={14} /> Retenir
                    </button>
                    <button data-testid={`reject-coach-${c.coach_user_id}`} onClick={() => decide(e.id, c.coach_user_id, "rejected")}
                      className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${c.status === "rejected" ? "bg-destructive text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      <XCircle size={14} /> Refuser
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

/* --- Mon enfant : suivi parent pour un compte staff (coach/admin) aussi parent --- */
const MyChildPanel = () => {
  const { hasRole } = useAuth();
  const [summary, setSummary] = useState(null);
  const enabled = hasRole("parent") || hasRole("player");
  const load = useCallback(() => {
    if (!enabled) return;
    api.get("/parent/week-summary").then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [enabled]);
  useEffect(() => { load(); }, [load]);
  const declare = async (event_id, member_id, declared) => {
    setSummary((s) => s && ({ ...s, events: (s.events || []).map((e) => e.id === event_id
      ? { ...e, participants: e.participants.map((p) => p.member_id === member_id ? { ...p, declared } : p) } : e) }));
    try { await api.post("/attendance/declare", { event_id, member_id, declared }); load(); }
    catch { toast.error("Erreur"); load(); }
  };
  if (!enabled || !summary || !(summary.events || []).length) return null;
  return (
    <div className="mb-8" data-testid="my-child-panel">
      <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
        <Users size={20} className="text-primary" /> Mon enfant — cette semaine
      </h2>
      <div className="space-y-3">
        {summary.events.map((e) => (
          <Card key={e.id} data-testid="my-child-event">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge color={EVENT_COLORS[e.type]}>{EVENT_LABELS[e.type] || "Événement"}</Badge>
              <span className="font-bold text-sm">{e.title}</span>
              <span className="text-xs text-muted-foreground">{fmtDate(e.date)}{e.time ? ` · ${e.time}` : ""}{e.location ? ` · ${e.location}` : ""}</span>
            </div>
            {e.participants.map((p) => (
              <div key={p.member_id} className="flex items-center justify-between gap-2 py-1.5">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{p.member_name}</p>
                  {p.declared && <p className="text-[11px] text-muted-foreground">{p.declared === "present" ? "Présent" : "Absent"}</p>}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button data-testid={`mychild-present-${p.member_id}`} onClick={() => declare(e.id, p.member_id, "present")}
                    className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${p.declared === "present" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    <CheckCircle2 size={14} /> Présent
                  </button>
                  <button data-testid={`mychild-absent-${p.member_id}`} onClick={() => declare(e.id, p.member_id, "absent")}
                    className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${p.declared === "absent" ? "bg-destructive text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    <XCircle size={14} /> Absent
                  </button>
                </div>
              </div>
            ))}
          </Card>
        ))}
      </div>
      <Link to="/cotisations" className="text-sm text-primary hover:underline mt-3 inline-block" data-testid="mychild-financier-link">Financier de mon enfant →</Link>
    </div>
  );
};


/* ---------------- Admin (inchangé) ---------------- */
const AdminDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/alerts/coach").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  const stats = [
    { label: "Membres", value: data?.members, icon: Users, color: "text-primary", to: "/membres" },
    { label: "Essais", value: data?.trials, icon: FlaskConical, color: "text-purple-400", to: "/membres" },
    { label: "Catégories", value: data?.categories, icon: Tags, color: "text-sky-400", to: "/categories" },
    { label: "Cotisations payées", value: data?.paid, icon: CheckCircle2, color: "text-primary", to: "/cotisations" },
    { label: "En attente", value: data?.unpaid, icon: AlertCircle, color: "text-yellow-500", to: "/cotisations" },
  ];

  return (
    <div>
      <PageHeader title={`Bonjour, ${user?.name?.split(" ")[0] || ""}`} subtitle="Vue d'ensemble de votre club de futsal." />
      <MyChildPanel />
      <AdminAvailabilitySection />
      {alerts.length > 0 && (
        <div data-testid="coach-alert" className="mb-8 rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-5 animate-fade-up">
          <div className="flex items-center gap-2 mb-3">
            <BellRing className="text-yellow-500" size={20} />
            <h3 className="font-display font-bold uppercase tracking-tight text-yellow-500">À confirmer aujourd'hui</h3>
          </div>
          <div className="space-y-2">
            {alerts.map((a) => (
              <Link key={a.event_id} to="/presence" data-testid="alert-row"
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-background/50 hover:bg-background transition-colors">
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate">{a.title} {a.time ? `· ${a.time}` : ""} {a.category ? `· ${a.category}` : ""}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.pending_names.join(", ")}</p>
                </div>
                <Badge color="yellow">{a.pending_count} à confirmer</Badge>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {stats.map((s, i) => (
          <Link key={i} to={s.to || "#"} data-testid={`stat-card-${i}`} className="block">
            <Card className="hover:-translate-y-1 hover:border-primary/40 transition-all duration-200 cursor-pointer h-full">
              <s.icon className={s.color} size={24} />
              <p className="font-display text-4xl font-black mt-3">{s.value ?? "—"}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</p>
            </Card>
          </Link>
        ))}
      </div>
      <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
        <CalendarDays size={20} className="text-primary" /> Prochains rendez-vous
      </h2>
      {data?.upcoming?.length ? (
        <div className="space-y-3">
          {data.upcoming.map((e) => (
            <Card key={e.id} className="flex items-center justify-between hover:border-primary/40 transition-colors">
              <div className="flex items-center gap-4">
                <div className="text-center w-14 shrink-0">
                  <p className="font-display text-2xl font-black text-primary leading-none">{e.date?.slice(8, 10)}</p>
                  <p className="text-xs uppercase text-muted-foreground">{e.date?.slice(5, 7)}/{e.date?.slice(2, 4)}</p>
                </div>
                <div>
                  <p className="font-bold">{e.title}</p>
                  <p className="text-xs text-muted-foreground">{e.time || ""} {e.location ? `· ${e.location}` : ""} {e.opponent ? `· vs ${e.opponent}` : ""}</p>
                </div>
              </div>
              <Badge color={EVENT_COLORS[e.type]}>{EVENT_LABELS[e.type]}</Badge>
            </Card>
          ))}
        </div>
      ) : (
        <Empty text="Aucun rendez-vous à venir." />
      )}
    </div>
  );
};
